/**
 * Buy Now Anchor Button
 * A bottom-pinned "Buy Now" button on the product page. It shows while the
 * shopper has NOT yet scrolled down to the real Add to Cart button, and
 * hides once they have. Clicking it smooth-scrolls to the purchase area
 * (the Kaching bundle), or to a custom section the merchant picked.
 */
(function () {
  'use strict';

  // The real Add to Cart button, in order of preference.
  // The first one that exists on the page wins.
  var ADD_TO_CART_SELECTORS = [
    '.main-product-atc',
    'product-form button[name="add"]',
    'form[action*="/cart/add"] button[type="submit"]',
  ];

  /**
   * One BuyNowAnchor instance manages ONE button block on the page.
   * Everything it needs to remember (its button, its mode, the element
   * it's watching) lives on `this`, instead of loose variables.
   */
  class BuyNowAnchor {
    constructor(blockElement) {
      // The outer wrapper div for this block.
      this.blockElement = blockElement;

      // The actual clickable button inside the wrapper.
      this.buttonElement = blockElement.querySelector('[data-buy-now-anchor-button]');

      // 'default' or 'custom' — set by the merchant in the theme editor.
      this.targetMode = blockElement.dataset.targetMode || 'default';

      // In custom mode, the CSS selector the merchant typed in (e.g. "#featuredCollection").
      this.customSelector = (blockElement.dataset.customTarget || '').trim();

      // The real Add to Cart button we watch to decide show/hide.
      this.addToCartButton = findAddToCartButton();

      // The "is this element on screen" watcher. Created in start().
      this.visibilityWatcher = null;

      // Bound once so we can add AND remove the same click handler reference.
      this.handleClick = this.handleClick.bind(this);
    }

    /**
     * Sets everything up. Called once per block.
     * If there's no button or no Add to Cart on the page, we quietly do nothing.
     */
    start() {
      if (!this.buttonElement) return;
      if (!this.addToCartButton) return; // Nothing to anchor to.

      this.watchAddToCartVisibility(this.addToCartButton);
      this.watchForFormReplacement();
      this.buttonElement.addEventListener('click', this.handleClick);
    }

    /**
     * Watches the Add to Cart button and shows/hides our floating button.
     *
     * Uses IntersectionObserver — the browser's built-in "is this element
     * visible" tool. It only runs our code when the answer CHANGES, not on
     * every scroll, so it's very light on performance.
     */
    watchAddToCartVisibility(addToCartButton) {
      // If we were already watching something, stop first.
      if (this.visibilityWatcher) {
        this.visibilityWatcher.disconnect();
      }

      this.visibilityWatcher = new IntersectionObserver(
        (entries) => {
          var report = entries[0];

          // Add to Cart is ON screen -> shopper reached it -> hide our button.
          if (report.isIntersecting) {
            this.hide();
            return;
          }

          // Add to Cart is OFF screen. Which way?
          //   top > 0  -> it's BELOW the viewport -> shopper not there yet -> SHOW.
          //   top <= 0 -> it's ABOVE the viewport -> shopper scrolled past -> HIDE.
          var addToCartIsBelowViewport = report.boundingClientRect.top > 0;
          if (addToCartIsBelowViewport) {
            this.show();
          } else {
            this.hide();
          }
        },
        { threshold: 0 }
      );

      this.visibilityWatcher.observe(addToCartButton);
    }

    /**
     * The Kaching bundle app rebuilds the product form when a shopper
     * switches bundle tier (Buy One / Buy Two / Buy Three). That can
     * replace the Add to Cart button with a fresh copy, leaving our
     * watcher pointed at a button that no longer exists.
     *
     * This watches the form's container for changes and re-points the
     * visibility watcher at the new button when that happens.
     */
    watchForFormReplacement() {
      var productForm =
        this.addToCartButton.closest('product-form') ||
        this.addToCartButton.closest('form');

      if (!productForm || !productForm.parentNode) return;

      var formChangeWatcher = new MutationObserver(() => {
        var freshAddToCartButton = findAddToCartButton();
        if (freshAddToCartButton && freshAddToCartButton !== this.addToCartButton) {
          this.addToCartButton = freshAddToCartButton;
          this.watchAddToCartVisibility(this.addToCartButton);
        }
      });

      formChangeWatcher.observe(productForm.parentNode, {
        childList: true,
        subtree: true,
      });
    }

    /**
     * Runs when the shopper clicks the floating button.
     * Custom mode and default mode are kept completely separate.
     */
    handleClick() {
      var headerOffset =
        (parseInt(
          getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
          10
        ) || 150) - 150;

      if (this.targetMode === 'custom') {
        if (!this.customSelector) {
          console.warn('[buy-now-anchor] Custom mode but no target selector set.');
          if (this.isFloating) this.hide();
          return;
        }

        var customElement = null;
        try {
          customElement = document.querySelector(this.customSelector);
        } catch (error) {
          console.warn('[buy-now-anchor] Invalid custom selector:', this.customSelector, error);
        }

        if (customElement) scrollToElement(customElement, headerOffset);
        if (this.isFloating) this.hide();
        return;
      }

      var kachingBundle = document.querySelector('kaching-bundle');
      if (kachingBundle) {
        scrollToElement(kachingBundle, headerOffset);
      } else {
        var productForm =
          this.addToCartButton.closest('product-form') ||
          this.addToCartButton.closest('form') ||
          this.addToCartButton;
        scrollToElement(productForm, headerOffset);
      }

      if (this.isFloating) this.hide();
    }

    /** Shows the floating button. */
    show() {
      this.blockElement.classList.add('is-visible');
    }

    /** Hides the floating button. */
    hide() {
      this.blockElement.classList.remove('is-visible');
    }
  }

  /**
   * Finds the real Add to Cart button by trying each selector in order.
   * Returns the first match, or null if none exist on the page.
   */
  function findAddToCartButton() {
    for (var i = 0; i < ADD_TO_CART_SELECTORS.length; i++) {
      var element = document.querySelector(ADD_TO_CART_SELECTORS[i]);
      if (element) return element;
    }
    return null;
  }

  /**
   * Smooth-scrolls the page so the TOP of `element` sits just below the
   * sticky header. Measured at click time, so it's correct no matter how
   * the page content above has shifted.
   */
  function scrollToElement(element, headerOffset) {
    // getBoundingClientRect().top = the element's top, measured from the
    //   top of the visible area.
    // + window.scrollY            = converts that into a position on the
    //   whole page (not just the visible part).
    // - headerOffset              = leaves room for the sticky header.
    var pagePosition =
      element.getBoundingClientRect().top + window.scrollY - headerOffset;

    window.scrollTo({
      top: Math.max(0, pagePosition), // never scroll above the top of the page
      behavior: 'smooth',
    });
  }

  /**
   * Finds every Buy Now Anchor block on the page and starts each one.
   */
  function startAllBlocks() {
    var blocks = document.querySelectorAll('[data-buy-now-anchor]');
    for (var i = 0; i < blocks.length; i++) {
      var anchor = new BuyNowAnchor(blocks[i]);
      anchor.start();
    }
  }

  // Wait for the page to be ready before running.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startAllBlocks);
  } else {
    startAllBlocks();
  }

  // The theme editor reloads sections without a full page refresh —
  // re-run when that happens so the button keeps working while editing.
  document.addEventListener('shopify:section:load', startAllBlocks);
})();